<template>
    <div class="flex h-full min-h-0 flex-col">
        <div class="border-b border-gray-200 p-4">
            <AtomicButton
                class="w-full"
                color="yellow"
                size="sm"
                @click="$emit('new-chat')"
            >
                {{ t("vue.ai_chat_new_chat") }}
            </AtomicButton>

            <div class="relative mt-3 flex items-center gap-2">
                <TextInput
                    v-model="search"
                    class="flex-1"
                    :placeholder="t('vue.ai_chat_search')"
                />
                <button
                    type="button"
                    class="relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition hover:bg-gray-50"
                    :class="{
                        'border-[#FF421E] text-[#FF421E]':
                            selectedTagIds.length > 0,
                    }"
                    :title="t('vue.ai_tags_filter')"
                    @click="filterOpen = !filterOpen"
                >
                    <FunnelIcon class="h-5 w-5" />
                    <span
                        v-if="selectedTagIds.length > 0"
                        class="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FF421E] px-1 text-[10px] font-semibold text-white"
                    >
                        {{ selectedTagIds.length }}
                    </span>
                </button>

                <div
                    v-if="filterOpen"
                    class="absolute right-0 top-full z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
                >
                    <p
                        v-if="filterableTags.length === 0"
                        class="px-2 py-2 text-xs text-gray-400"
                    >
                        {{ t("vue.ai_tags_none") }}
                    </p>
                    <label
                        v-for="tag in filterableTags"
                        :key="tag.id ?? tag.slug"
                        class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-gray-50"
                    >
                        <input
                            type="checkbox"
                            :checked="selectedTagIds.includes(tag.id as number)"
                            class="h-4 w-4 rounded border-gray-300 text-[#FF421E] focus:ring-[#FF421E]"
                            @change="toggleFilter(tag.id as number)"
                        />
                        <span class="text-sm text-gray-700">{{
                            tag.name
                        }}</span>
                    </label>
                </div>
            </div>
        </div>

        <div class="flex-1 overflow-y-auto p-2">
            <p
                v-if="chats.length === 0"
                class="px-2 py-4 text-sm text-gray-400"
            >
                {{ t("vue.ai_chat_no_chats") }}
            </p>

            <div v-for="section in sections" :key="section.key">
                <p
                    v-if="showSectionHeaders && section.items.length > 0"
                    class="px-2 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wide text-gray-400"
                >
                    {{ section.label }}
                </p>

                <div
                    v-for="chat in section.items"
                    :key="chat.id"
                    class="group relative mb-1"
                >
                    <div
                        v-if="editingId === chat.id"
                        class="flex items-center gap-1 rounded-lg bg-gray-100 px-2 py-1.5"
                    >
                        <input
                            v-model="editName"
                            type="text"
                            class="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-400 focus:outline-hidden"
                            @keyup.enter="saveRename(chat.id)"
                            @keyup.esc="cancelRename"
                        />
                        <button
                            type="button"
                            class="shrink-0 cursor-pointer text-green-600 hover:text-green-700"
                            @click="saveRename(chat.id)"
                        >
                            <CheckIcon class="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            class="shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
                            @click="cancelRename"
                        >
                            <XMarkIcon class="h-4 w-4" />
                        </button>
                    </div>

                    <template v-else>
                        <inertia-link
                            :href="linkFor(chat.id)"
                            preserve-scroll
                            class="flex w-full items-center gap-2 rounded-lg py-2 pl-3 pr-14 text-left transition-colors"
                            :class="
                                chat.id === activeChatId
                                    ? 'bg-gray-100 font-medium text-gray-800'
                                    : 'text-gray-600 hover:bg-gray-50'
                            "
                        >
                            <ChatBubbleLeftRightIcon
                                class="h-4 w-4 shrink-0 text-gray-400"
                            />
                            <span class="min-w-0 flex-1 truncate text-sm">{{
                                chat.title
                            }}</span>
                        </inertia-link>

                        <div
                            class="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"
                        >
                            <button
                                type="button"
                                class="cursor-pointer rounded p-1 transition"
                                :class="
                                    chat.pinned
                                        ? 'text-[#FF421E]'
                                        : 'text-gray-400 opacity-0 hover:text-gray-700 group-hover:opacity-100'
                                "
                                :title="
                                    chat.pinned
                                        ? t('vue.ai_chat_unpin')
                                        : t('vue.ai_chat_pin')
                                "
                                @click="togglePin(chat.id)"
                            >
                                <Pin
                                    class="h-4 w-4"
                                    :class="chat.pinned ? 'fill-current' : ''"
                                />
                            </button>
                            <button
                                type="button"
                                class="cursor-pointer rounded p-1 transition hover:text-gray-700"
                                :class="
                                    menuChat?.id === chat.id
                                        ? 'text-gray-700 opacity-100'
                                        : 'text-gray-400 opacity-0 group-hover:opacity-100'
                                "
                                :title="t('vue.ai_chat_more_actions')"
                                @click.stop.prevent="toggleMenu(chat, $event)"
                            >
                                <EllipsisVerticalIcon class="h-4 w-4" />
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- Row actions menu, teleported to the body so the list's own vertical
             overflow never clips it. Only one row's menu is open at a time. -->
        <Teleport to="body">
            <template v-if="menuChat !== null">
                <div class="fixed inset-0 z-[590]" @click="closeMenu" />
                <div
                    class="fixed z-[600] w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                    :style="menuStyle"
                >
                    <button
                        type="button"
                        class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50"
                        @click="renameFromMenu"
                    >
                        <PencilSquareIcon class="h-4 w-4 shrink-0" />
                        {{ t("vue.ai_chat_rename") }}
                    </button>
                    <button
                        v-if="canDelete"
                        type="button"
                        class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-red-600 transition hover:bg-red-50"
                        @click="deleteFromMenu"
                    >
                        <TrashIcon class="h-4 w-4 shrink-0" />
                        {{ t("vue.ai_chat_delete") }}
                    </button>
                </div>
            </template>
        </Teleport>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import {
    FunnelIcon,
    ChatBubbleLeftRightIcon,
    PencilSquareIcon,
    TrashIcon,
    EllipsisVerticalIcon,
    CheckIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";
import { Pin } from "lucide-vue-next";
import type { IAiChatListItem } from "@/types/AiChat/IAiChat";
import type { IAiTag } from "@/types/AiChat/IAiTag";
import { useTranslations } from "@/composables/useTranslations";
import useConfirm from "@/composables/useConfirm";

const props = defineProps<{
    chats: IAiChatListItem[];
    activeChatId: number | null;
    q: string | null;
    activeTagIds: number[];
    availableTags: IAiTag[];
}>();

defineEmits<{
    (event: "new-chat"): void;
}>();

const { t } = useTranslations();
const { show } = useConfirm();

// Trial (limited) teams may not delete chats — deletion cascades messages and
// would reset the trial message cap. Enforced server-side by AiChatPolicy.
const canDelete = computed(
    () => usePage<IRhizomPageProps>().props.aiChat?.level !== "limited",
);

// Split into Pinned / Recents; the server already returns pinned chats first.
const sections = computed(() => [
    {
        key: "pinned",
        label: t("vue.ai_chat_pinned"),
        items: props.chats.filter((chat) => chat.pinned),
    },
    {
        key: "recents",
        label: t("vue.ai_chat_recents"),
        items: props.chats.filter((chat) => !chat.pinned),
    },
]);

// Only show the section labels once something is actually pinned.
const showSectionHeaders = computed(() =>
    props.chats.some((chat) => chat.pinned),
);

const togglePin = (id: number): void => {
    router.post(
        route("ai-chat.pin", { chat: id }),
        {},
        { preserveScroll: true, preserveState: true },
    );
};

const search = ref(props.q ?? "");
const selectedTagIds = ref<number[]>([...props.activeTagIds]);
const filterOpen = ref(false);
let searchTimer: number | undefined;

const editingId = ref<number | null>(null);
const editName = ref("");

const startRename = (chat: IAiChatListItem): void => {
    editingId.value = chat.id;
    editName.value = chat.title;
};

const cancelRename = (): void => {
    editingId.value = null;
    editName.value = "";
};

const saveRename = (id: number): void => {
    if (editName.value.trim() === "") {
        return;
    }
    router.put(
        route("ai-chat.update", { chat: id }),
        { title: editName.value.trim() },
        { preserveScroll: true, preserveState: true, onSuccess: cancelRename },
    );
};

const deleteChat = async (id: number): Promise<void> => {
    const confirmed = await show({
        header: t("vue.ai_chat_delete"),
        message: t("vue.ai_chat_delete_confirm"),
        type: "danger",
        confirm: t("vue.ai_chat_delete"),
    });

    if (confirmed) {
        router.delete(route("ai-chat.destroy", { chat: id }), {
            preserveScroll: true,
        });
    }
};

// The kebab menu is teleported to the body and positioned under its trigger, so
// the scrolling list never clips it. Only one row's menu is open at a time.
const menuChat = ref<IAiChatListItem | null>(null);
const menuStyle = ref<Record<string, string>>({});

const toggleMenu = (chat: IAiChatListItem, event: MouseEvent): void => {
    if (menuChat.value?.id === chat.id) {
        closeMenu();
        return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    menuStyle.value = {
        top: `${rect.bottom + 4}px`,
        right: `${window.innerWidth - rect.right}px`,
    };
    menuChat.value = chat;
};

const closeMenu = (): void => {
    menuChat.value = null;
};

const renameFromMenu = (): void => {
    const chat = menuChat.value;
    closeMenu();
    if (chat !== null) {
        startRename(chat);
    }
};

const deleteFromMenu = (): void => {
    const chat = menuChat.value;
    closeMenu();
    if (chat !== null) {
        void deleteChat(chat.id);
    }
};

// Only persisted team tags (with an id) can filter chats.
const filterableTags = computed<IAiTag[]>(() =>
    props.availableTags.filter((tag) => tag.id !== null),
);

const visit = (): void => {
    router.get(
        activeUrl(),
        { q: search.value, tags: selectedTagIds.value },
        { preserveState: true, preserveScroll: true },
    );
};

const activeUrl = (): string =>
    props.activeChatId !== null
        ? route("ai-chat.show", { chat: props.activeChatId })
        : route("ai-chat.index");

const linkFor = (id: number): string =>
    route("ai-chat.show", {
        chat: id,
        q: search.value,
        tags: selectedTagIds.value,
    });

const toggleFilter = (id: number): void => {
    selectedTagIds.value = selectedTagIds.value.includes(id)
        ? selectedTagIds.value.filter((current) => current !== id)
        : [...selectedTagIds.value, id];
    visit();
};

watch(search, () => {
    if (searchTimer !== undefined) {
        window.clearTimeout(searchTimer);
    }
    searchTimer = window.setTimeout(visit, 300);
});
</script>
